import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import {
  Card, Button, Modal, Input, Select, PageLoader, StatusBadge, IconButton,
  AlertBanner, BackButton, StickyActions,
} from "../components/UI";
import { generateInvoicePDF } from "../utils/invoicePDF";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";
import toast from "react-hot-toast";
import { Printer, Send, CircleCheckBig, Pencil, Trash2, Lock, MapPin, TriangleAlert, Receipt } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminBillDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payModal, setPayModal] = useState(false);
  const [payForm, setPayForm] = useState({ paidAmount: "", paidDate: new Date().toISOString().split("T")[0], paymentMode: "NEFT", paymentRef: "" });
  const [saving, setSaving] = useState(false);

  const fetchBill = useCallback(async () => {
    try {
      const { data } = await api.get(`/bills/${id}`);
      setBill(data.data);
      setPayForm(f => ({ ...f, paidAmount: data.data.grandTotal }));
    } catch { toast.error("Bill not found"); navigate("/admin/bills"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { fetchBill(); }, [fetchBill]);

  const handleStatus = async (status) => {
    try {
      const { data } = await api.put(`/bills/${id}/status`, { status });
      setBill(data.data);
      toast.success(`Bill marked as ${status}`);
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
  };

  const handlePay = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`/bills/${id}/pay`, payForm);
      setBill(data.data);
      setPayModal(false);
      toast.success("Payment recorded — bill locked");
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this bill permanently?")) return;
    try {
      await api.delete(`/bills/${id}`);
      toast.success("Bill deleted");
      navigate("/admin/bills");
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
  };

  if (loading) return <AdminLayout><PageLoader label="Loading invoice…" /></AdminLayout>;
  if (!bill) return null;

  const editable = !bill.isLocked;

  return (
    <AdminLayout>
      <div className="space-y-5 max-w-5xl mx-auto">

        {/* ══ Header ══ */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <BackButton to="/admin/bills" />
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-bold text-ink-900 font-mono">{bill.billNumber}</h1>
                <StatusBadge status={bill.status} />
                {bill.isLocked && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-200 px-2 py-1 rounded-full font-medium">
                    <Lock size={11} /> Locked
                  </span>
                )}
              </div>
              <p className="text-ink-400 text-sm mt-1">
                {bill.billDate}{bill.dueDate ? ` · Due ${bill.dueDate}` : ""}
              </p>
            </div>
          </div>

          <div className="flex gap-1.5 flex-wrap lg:justify-end no-print">
            <Button variant="secondary" onClick={() => generateInvoicePDF(bill, { signatureUrl: user?.signatureUrl, sealUrl: user?.sealUrl })} icon={<Printer size={15} />}>Print / PDF</Button>
            {editable && bill.status === "draft" && (
              <Button variant="secondary" onClick={() => handleStatus("sent")} icon={<Send size={15} />}>Mark Sent</Button>
            )}
            {editable && (bill.status === "sent" || bill.status === "overdue") && (
              <Button variant="success" onClick={() => setPayModal(true)} icon={<CircleCheckBig size={15} />}>Record Payment</Button>
            )}
            {editable && bill.status === "sent" && (
              <Button variant="ghost" onClick={() => handleStatus("overdue")} className="text-red-600">Mark Overdue</Button>
            )}
            {editable && <IconButton variant="outline" icon={<Pencil size={16} />} to={`/admin/bills/${id}/edit`} title="Edit invoice" />}
            {editable && <IconButton variant="danger" icon={<Trash2 size={16} />} onClick={handleDelete} title="Delete invoice" />}
          </div>
        </div>

        {/* ══ Parties ══ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="p-4 sm:p-5">
            <div className="text-[11px] text-ink-400 uppercase tracking-wider font-semibold mb-2">Bill To</div>
            <div className="text-ink-900 font-semibold">{bill.clientId?.name || "—"}</div>
            {bill.clientId?.company && <div className="text-ink-500 text-sm">{bill.clientId.company}</div>}
            {bill.clientId?.email && <div className="text-ink-400 text-xs mt-1 break-all">{bill.clientId.email}</div>}
            {bill.clientId?.phone && <div className="text-ink-400 text-xs">{bill.clientId.phone}</div>}
            {bill.clientId?.gstin && <div className="text-ink-500 text-xs mt-1.5 font-mono">GSTIN: {bill.clientId.gstin}</div>}
          </Card>

          <Card className="p-4 sm:p-5">
            <div className="text-[11px] text-ink-400 uppercase tracking-wider font-semibold mb-2">
              {bill.siteId ? "Site / Project" : "From"}
            </div>
            <div className="text-ink-900 font-semibold">{bill.companyName || "—"}</div>
            {bill.siteId && (
              <div className="text-ink-500 text-sm flex items-center gap-1.5 mt-0.5">
                <MapPin size={13} className="shrink-0" />
                <span className="truncate">{bill.siteId.name}{bill.siteId.code ? ` (${bill.siteId.code})` : ""}</span>
              </div>
            )}
            {bill.subject && <div className="text-ink-500 text-sm mt-0.5">{bill.subject}</div>}
            {bill.companyGST && <div className="text-ink-500 text-xs mt-1.5 font-mono">GSTIN: {bill.companyGST}</div>}
            {bill.companyAddress && <div className="text-ink-400 text-xs mt-0.5">{bill.companyAddress}</div>}
          </Card>
        </div>

        {/* ══ Line items ══ */}
        <Card className="overflow-hidden">
          {/* desktop table */}
          <div className="hidden sm:block">
            {/* fixed layout: the description absorbs the slack, so the invoice never scrolls sideways */}
            <table className="w-full table-fixed text-sm">
              <thead className="bg-ink-50/80 border-b border-ink-200/70">
                <tr>
                  <th className="text-left px-5 py-3 text-[11px] text-ink-500 uppercase font-semibold w-10">#</th>
                  <th className="text-left px-4 py-3 text-[11px] text-ink-500 uppercase font-semibold whitespace-nowrap">Date</th>
                  <th className="text-left px-4 py-3 text-[11px] text-ink-500 uppercase font-semibold">Description</th>
                  <th className="text-center px-4 py-3 text-[11px] text-ink-500 uppercase font-semibold w-24">Qty</th>
                  <th className="text-right px-4 py-3 text-[11px] text-ink-500 uppercase font-semibold w-28">Rate</th>
                  <th className="text-right px-5 py-3 text-[11px] text-ink-500 uppercase font-semibold w-32">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {bill.items?.map((item, i) => (
                  <tr key={i} className="hover:bg-ink-50/60">
                    <td className="px-5 py-3 text-ink-400 tnum">{i + 1}</td>
                    <td className="px-4 py-3 text-ink-400 whitespace-nowrap">{item.date}</td>
                    <td className="px-4 py-3">
                      <div className="text-ink-800">{item.description}</div>
                      {item.hsnCode && <div className="text-ink-400 text-xs font-mono">HSN: {item.hsnCode}</div>}
                    </td>
                    <td className="px-4 py-3 text-center text-ink-700 tnum">{item.quantity} {item.unit}</td>
                    <td className="px-4 py-3 text-right text-ink-700 font-mono tnum">{money(item.rate)}</td>
                    <td className="px-5 py-3 text-right text-ink-900 font-semibold font-mono tnum">{money(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* mobile item cards */}
          <ul className="sm:hidden divide-y divide-ink-100">
            {bill.items?.map((item, i) => (
              <li key={i} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-ink-800 text-sm">{item.description}</div>
                    <div className="text-ink-400 text-xs mt-0.5">
                      {item.date}{item.hsnCode ? ` · HSN ${item.hsnCode}` : ""}
                    </div>
                  </div>
                  <div className="text-ink-900 font-semibold font-mono text-sm shrink-0 tnum">{money(item.amount)}</div>
                </div>
                <div className="text-xs text-ink-500 mt-1.5 tnum">
                  {item.quantity} {item.unit} × {money(item.rate)}
                </div>
              </li>
            ))}
          </ul>

          {/* totals */}
          <div className="flex justify-end p-4 sm:p-5 border-t border-ink-100 bg-ink-50/40">
            <div className="w-full sm:w-72 space-y-2 text-sm">
              <div className="flex justify-between text-ink-500"><span>Sub Total</span><span className="font-mono tnum">{money(bill.subTotal)}</span></div>
              {bill.discount > 0 && (
                <>
                  <div className="flex justify-between text-red-600"><span>Discount</span><span className="font-mono tnum">− {money(bill.discount)}</span></div>
                  <div className="flex justify-between text-ink-500 border-t border-ink-200 pt-2"><span>Taxable</span><span className="font-mono tnum">{money(bill.taxableAmount)}</span></div>
                </>
              )}
              {bill.cgst > 0 && <div className="flex justify-between text-indigo-600"><span>CGST @ {bill.gstRate / 2}%</span><span className="font-mono tnum">{money(bill.cgst)}</span></div>}
              {bill.sgst > 0 && <div className="flex justify-between text-indigo-600"><span>SGST @ {bill.gstRate / 2}%</span><span className="font-mono tnum">{money(bill.sgst)}</span></div>}
              {bill.igst > 0 && <div className="flex justify-between text-indigo-600"><span>IGST @ {bill.gstRate}%</span><span className="font-mono tnum">{money(bill.igst)}</span></div>}

              <div className="flex justify-between items-center border-t-2 border-ink-200 pt-3">
                <span className="font-bold text-ink-900">Grand Total</span>
                <span className="text-lg font-bold text-emerald-600 font-display tnum">{money(bill.grandTotal)}</span>
              </div>

              {bill.status === "paid" && (
                <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <div className="flex justify-between text-emerald-700 font-semibold"><span>Paid</span><span className="tnum">{money(bill.paidAmount)}</span></div>
                  <div className="text-emerald-600 text-xs mt-1">
                    {bill.paidDate} · {bill.paymentMode}{bill.paymentRef ? ` · Ref ${bill.paymentRef}` : ""}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* ══ Notes / terms ══ */}
        {(bill.notes || bill.terms) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {bill.notes && (
              <Card className="p-4">
                <div className="text-[11px] text-ink-400 uppercase tracking-wider font-semibold mb-2">Notes</div>
                <p className="text-ink-700 text-sm whitespace-pre-wrap leading-relaxed">{bill.notes}</p>
              </Card>
            )}
            {bill.terms && (
              <Card className="p-4">
                <div className="text-[11px] text-ink-400 uppercase tracking-wider font-semibold mb-2">Terms</div>
                <p className="text-ink-700 text-sm whitespace-pre-wrap leading-relaxed">{bill.terms}</p>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* ══ Record payment ══ */}
      <Modal open={payModal} onClose={() => setPayModal(false)} title="Record Payment"
        subtitle={bill.billNumber} icon={<Receipt size={18} />}>
        <form onSubmit={handlePay} className="space-y-4">
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-50/40 border border-emerald-200 rounded-2xl p-4 text-center">
            <div className="text-ink-500 text-xs uppercase tracking-wider font-semibold">Invoice total</div>
            <div className="text-3xl font-bold font-display text-emerald-600 mt-1 tnum">{money(bill.grandTotal)}</div>
          </div>

          <Input label="Amount Received" type="number" min="0" step="0.01" required suffix="₹"
            value={payForm.paidAmount} onChange={e => setPayForm({ ...payForm, paidAmount: e.target.value })} />
          <Input label="Payment Date" type="date" required
            value={payForm.paidDate} onChange={e => setPayForm({ ...payForm, paidDate: e.target.value })} />
          <Select label="Payment Mode" value={payForm.paymentMode} onChange={e => setPayForm({ ...payForm, paymentMode: e.target.value })}>
            {["NEFT", "RTGS", "UPI", "Cheque", "Cash", "IMPS", "Bank Transfer"].map(m => <option key={m} value={m}>{m}</option>)}
          </Select>
          <Input label="Reference / UTR / Cheque No." placeholder="Optional"
            value={payForm.paymentRef} onChange={e => setPayForm({ ...payForm, paymentRef: e.target.value })} />

          <AlertBanner type="warning" message="Recording payment locks this bill — it can't be edited afterwards." />

          <StickyActions>
            <Button type="button" variant="secondary" onClick={() => setPayModal(false)} block className="sm:w-auto">Cancel</Button>
            <Button type="submit" variant="success" loading={saving} icon={<CircleCheckBig size={16} />} block className="sm:w-auto">
              Confirm Payment
            </Button>
          </StickyActions>
        </form>
      </Modal>
    </AdminLayout>
  );
}
